import { toast } from 'sonner';
import { networkMonitor } from '@/lib/networkUtils';
import i18n from '@/lib/i18n';
import { captureError } from '@/lib/sentry';

export type ErrorLevel = 'info' | 'warn' | 'error' | 'fatal';

export interface AppError extends Error {
  code?: string;
  level?: ErrorLevel;
  context?: Record<string, any>;
}

class ErrorHandler {
  private static instance: ErrorHandler;
  private errorLog: AppError[] = [];

  private constructor() {}

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  handle(error: Error | AppError | any, context?: Record<string, any>): void {
    // Ensure we always work with a proper Error instance
    const realError: AppError = error instanceof Error
      ? error
      : Object.assign(new Error(error?.message || String(error)), {
          name: error?.name || 'AppError',
        });

    realError.code = (error as AppError)?.code ?? realError.code;
    realError.level = (error as AppError)?.level || 'error';
    realError.context = { ...(error as AppError)?.context, ...context };

    this.errorLog.push(realError);
    console.error('Application Error:', realError);

    this.showUserNotification(realError);

    if (process.env.NODE_ENV === 'production') {
      this.reportError(realError);
    }
  }

  private showUserNotification(error: AppError): void {
    const t = (key: string) => i18n.t(key);
    
    if (!networkMonitor.isOnline) {
      toast.error(t('toasts.errorHandler.noInternet'));
      return;
    }
    
    const sanitizeMessage = (msg: string) => {
      const div = document.createElement('div');
      div.textContent = msg;
      return div.innerHTML;
    };
    
    switch (error.level) {
      case 'fatal':
        toast.error(t('toasts.errorHandler.criticalError'));
        break;
      case 'error':
        const errorMsg = error.message || t('toasts.errorHandler.defaultError');
        toast.error(sanitizeMessage(errorMsg));
        break;
      case 'warn':
        const warnMsg = error.message || t('toasts.errorHandler.defaultWarning');
        toast.warning(sanitizeMessage(warnMsg));
        break;
      case 'info':
        toast.info(sanitizeMessage(error.message));
        break;
      default:
        toast.error(t('toasts.errorHandler.unexpectedError'));
    }
  }

  private reportError(error: AppError): void {
    captureError(error, {
      code: error.code,
      level: error.level,
      ...error.context,
    });
  }

  getErrorLog(): AppError[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }
}

export const errorHandler = ErrorHandler.getInstance();

export const handleApiError = (error: any, operation: string): void => {
  const appError = Object.assign(new Error(error?.message || `Failed to ${operation}`), {
    name: 'ApiError',
    code: error?.code || 'API_ERROR',
    level: 'error' as ErrorLevel,
    context: { operation, originalError: error },
  });
  errorHandler.handle(appError);
};

export const handleFormError = (error: any, fieldName?: string): void => {
  const appError = Object.assign(new Error(error?.message || 'Form validation failed'), {
    name: 'FormError',
    code: 'FORM_ERROR',
    level: 'warn' as ErrorLevel,
    context: { fieldName, originalError: error },
  });
  errorHandler.handle(appError);
};

export const handleNetworkError = (error: any): void => {
  const t = (key: string) => i18n.t(key);
  const appError = Object.assign(new Error(t('toasts.errorHandler.noInternet')), {
    name: 'NetworkError',
    code: 'NETWORK_ERROR',
    level: 'error' as ErrorLevel,
    context: { originalError: error },
  });
  errorHandler.handle(appError);
};